import get from 'lodash.get';
import React, { Component, Fragment } from 'react';
import { flowRight as compose } from 'lodash';
import Helmet from 'react-helmet';
import { intlShape, injectIntl } from 'react-intl';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import Text from '../../components/BuildingBlocks/Text';
import withLocaleProvider from '../../hocs/withLocaleProvider';
import withRouting, { withRoutingPropType } from '../../hocs/withRouting';
import withAuth, { withAuthPropType } from '../../hocs/withAuth';

import Button from '../../components/BuildingBlocks/Button';
import Grid from '../../components/Utility/Grid';
import VerticalSpacing from '../../components/Utility/VerticalSpacing';
import NoSSR from '../../components/NoSSR';
import PageSEO from '../../components/PageSEO';
import MapContainer from '../../components/Map/MapContainer';
import LocaleLink from '../../components/LocaleLink';
import messages from '../../messages/Map/messages';
import { usePropertySearchContext } from '../../hooks/usePropertySearchContext';
import {
  useMapFiltersContext,
  DEFAULT_PRICE_RANGE_MIN,
  DEFAULT_PRICE_RANGE_MAX,
  DEFAULT_DISTANCE,
  DEFAULT_SAVED_AMENITIES,
  DEFAULT_SAVED_PROPERTY_TYPES,
  DEFAULT_SAVED_LAYERS
} from '../../hooks/useMapFiltersContext';

/**
 * Map Page
 */
class Map extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
    router: withRoutingPropType.isRequired,
    auth: withAuthPropType.isRequired,
    setMapFilters: PropTypes.func.isRequired,
    setSearchCriteria: PropTypes.func.isRequired
  };

  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = { showMapContainer: true };

    if (props.router.query.location) {
      this.state.showMapContainer = false; // Await link-state update from query params
    }
  }

  componentDidMount() {
    const {
      router: {
        query: { location }
      }
    } = this.props;

    moment.tz.setDefault();

    if (location) {
      this.updateLinkStateFromParams();
    }

    // disable scrolling
    document.body.classList.add('no-scrolling-map');
  }

  componentWillUnmount() {
    // ensure scrolling is re-enabled
    document.body.classList.remove('no-scrolling-map');
  }

  updateLinkStateFromParams = async () => {
    const {
      router: {
        query: {
          location,
          locationType,
          searchType,
          checkin,
          checkout,
          numberRooms,
          propertyTypes,
          minRates,
          maxRates,
          layers,
          amenity,
          distanceFrom
        }
      }
    } = this.props;
    const { setMapFilters, setSearchCriteria } = this.props;

    await Promise.all([
      setSearchCriteria({
        type: 'updateSearchLocationOnRedo',
        payload: {
          locationValue: location,
          locationType: locationType || searchType || 'Address'
        }
      }),
      setSearchCriteria({
        type: 'commitSearchTerm'
      }),
      checkin && checkout
        ? setSearchCriteria({
            type: 'updateDateCriteria',
            payload: {
              startDate: checkin,
              endDate: checkout
            }
          })
        : null,
      numberRooms
        ? setSearchCriteria({
            type: 'updateRoomCount',
            payload: {
              count: parseInt(numberRooms, 10)
            }
          })
        : null,
      layers
        ? setMapFilters({
            type: 'updateMapLayers',
            payload: {
              layers: layers
                ? layers.split(',').map(layer => ({ layerId: layer, selected: true }))
                : DEFAULT_SAVED_LAYERS.map(layer => ({ layerId: layer, selected: true }))
            }
          })
        : null,
      propertyTypes || minRates || maxRates || amenity || distanceFrom
        ? setMapFilters({
            type: 'updateMapFilters',
            payload: {
              mapFilters: {
                propertyTypes: propertyTypes
                  ? propertyTypes.split(',').map(type => ({ layerId: type, selected: true }))
                  : DEFAULT_SAVED_PROPERTY_TYPES.map(type => ({ layerId: type, selected: true })),
                amenities: amenity
                  ? amenity.split(',').map(type => ({ layerId: type, selected: true }))
                  : DEFAULT_SAVED_AMENITIES.map(type => ({ layerId: type, selected: true })),
                priceRangeMin: parseInt(minRates, 10) || DEFAULT_PRICE_RANGE_MIN,
                priceRangeMax: parseInt(maxRates, 10) || DEFAULT_PRICE_RANGE_MAX,
                distance: parseInt(distanceFrom, 10) || DEFAULT_DISTANCE
              }
            }
          })
        : null
    ]);

    this.setState({ showMapContainer: true });
  };

  // Friendly warning to identify an error within Map without interrupting or reloading
  componentDidCatch(e) {
    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable-next-line no-console */
      console.warn('An error within Map has occurred \n', 'See below:\n', e);
    }
  }

  render() {
    const { intl, router, auth } = this.props;
    const { showMapContainer } = this.state;
    const authenticated = get(auth, 'authenticated', false);

    return (
      <>
        <PageSEO title={intl.formatMessage(messages.seoTitle)} />
        <Helmet
          meta={[
            {
              name: 'viewport',
              content:
                'width=device-width, height=device-height, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no'
            }
          ]}
        />
        {authenticated && <NoSSR>{showMapContainer && <MapContainer router={router} auth={auth} />}</NoSSR>}

        {!authenticated && (
          <VerticalSpacing size="sm">
            <Grid.Container>
              <Grid.Row>
                <Grid.Col lg={12} align="center">
                  <Text.CopyBody tagName="div" align="center">
                    {intl.formatMessage(messages.bannerSubTextNotLoggedIn)}
                  </Text.CopyBody>
                  <LocaleLink route="SignUp" passHref>
                    <Button size="sm" block uppercase>
                      {intl.formatMessage(messages.notLoggedInCTAText)}
                    </Button>
                  </LocaleLink>
                  <Text.CopyBody tagName="div">
                    <Text.CopyBody tagName="span">{intl.formatMessage(messages.notLoggedInSignInText)}</Text.CopyBody>{' '}
                    <Text.CopyBody tagName="span" color="quaternary">
                      <LocaleLink route="SignIn" passHref>
                        <a href="#">{intl.formatMessage(messages.notLoggedInSignInCTAText)}</a>
                      </LocaleLink>
                    </Text.CopyBody>
                  </Text.CopyBody>
                </Grid.Col>
              </Grid.Row>
            </Grid.Container>
          </VerticalSpacing>
        )}
      </>
    );
  }
}

function MapPageFunctional(props) {
  const { setSearchCriteria } = usePropertySearchContext();
  const { mapFilters, setMapFilters } = useMapFiltersContext();

  return <Map {...props} mapFilters={mapFilters} setMapFilters={setMapFilters} setSearchCriteria={setSearchCriteria} />;
}

export default compose(
  withLocaleProvider,
  injectIntl,
  withRouting,
  withAuth()
)(MapPageFunctional);
